import { useTranslation } from 'react-i18next'
import { Gamepad2 } from 'lucide-react'
import type { ComponentProgressDetailView } from '@/types/coaching'
import type { CoachingGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import { CoachingComponentDetail } from './component-details/CoachingComponentDetail'
import type { QuizFocusRequest } from './grading-sheet/coachingFocus'
import { openGameMathPage } from './gameMathNavigation'

interface Props {
  component: ComponentProgressDetailView | null
  learnerId: number
  lessonId: number | null
  gameKeys?: string[]
  canEdit: boolean
  bare?: boolean
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
  onGradingModeChange?: (mode: CoachingGradingMode) => void
  gradingMode?: CoachingGradingMode
  onRequestCoaching?: (quizId: string) => void
  initialSelectedQuizId?: string | null
  focusRequest?: QuizFocusRequest | null
}

export function UnitDetailPanel({
  component,
  learnerId,
  lessonId,
  gameKeys = [],
  canEdit,
  gradingMode,
  onGradingModeChange,
  onRequestCoaching,
  initialSelectedQuizId,
  focusRequest,
  bare = false,
  draftResetKey,
  onDraftStateChange,
}: Props) {
  const { t } = useTranslation('coaching')

  if (!component) {
    return (
      <div className="bg-white border rounded-lg p-8 text-sm text-gray-500 text-center">
        {t('unit.selectPrompt')}
      </div>
    )
  }

  const gameKey = component.type === 'interactive' && component.interactive?.kind === 'game' ? gameKeys[0] : undefined

  const detailBody = (
    <CoachingComponentDetail
        component={component}
        learnerId={learnerId}
        lessonId={lessonId}
        canEdit={canEdit}
        gradingMode={gradingMode}
        onGradingModeChange={onGradingModeChange}
        onRequestCoaching={onRequestCoaching}
        initialSelectedQuizId={initialSelectedQuizId}
        focusRequest={focusRequest}
        bare={bare}
        draftResetKey={draftResetKey}
        onDraftStateChange={onDraftStateChange}
      />
  )
  if (bare) return detailBody

  return (
    <div className="space-y-3">
      {gameKey && (
        <button
          type="button"
          onClick={() => openGameMathPage(gameKey)}
          className="inline-flex items-center gap-1.5 rounded-md border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-700 hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-1"
        >
          <Gamepad2 size={14} /> {t('library.gameMathTag')}
        </button>
      )}
      {detailBody}
    </div>
  )
}
